import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';

const STORAGE_PREFIX = 'admin_department_options_';

function loadStoredDepartments(storageKey: string, defaults: string[]): string[] {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return defaults;
    const merged = [...defaults];
    for (const item of parsed) {
      const name = String(item || '').trim();
      if (!name) continue;
      if (!merged.some((d) => d.toLowerCase() === name.toLowerCase())) {
        merged.push(name);
      }
    }
    return merged;
  } catch {
    return defaults;
  }
}

function saveCustomDepartment(storageKey: string, defaults: string[], name: string) {
  const trimmed = name.trim();
  if (!trimmed) return;
  if (defaults.some((d) => d.toLowerCase() === trimmed.toLowerCase())) return;

  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    const existing: string[] = raw ? JSON.parse(raw) : [];
    const next = Array.isArray(existing) ? [...existing] : [];
    if (!next.some((d) => String(d).toLowerCase() === trimmed.toLowerCase())) {
      next.push(trimmed);
      localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, JSON.stringify(next));
    }
  } catch {
    // ignore storage errors
  }
}

interface DepartmentComboboxProps {
  value: string;
  onChange: (value: string) => void;
  defaultOptions: string[];
  storageKey: string;
  label?: string;
  placeholder?: string;
  inputClassName?: string;
  labelClassName?: string;
  isDark?: boolean;
  id?: string;
}

export function DepartmentCombobox({
  value,
  onChange,
  defaultOptions,
  storageKey,
  label = 'Department',
  placeholder = 'TYPE OR SELECT DEPARTMENT',
  inputClassName,
  labelClassName,
  isDark = false,
  id,
}: DepartmentComboboxProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(() => loadStoredDepartments(storageKey, defaultOptions));
  const [inputText, setInputText] = useState(value);

  useEffect(() => {
    setInputText(value);
  }, [value]);

  useEffect(() => {
    setOptions(loadStoredDepartments(storageKey, defaultOptions));
  }, [storageKey, defaultOptions]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const query = inputText.trim();
  const normalizedQuery = query.toLowerCase();

  const matchingOptions = useMemo(() => {
    if (!query) return options.slice(0, 50);
    return options
      .filter((option) => option.toLowerCase().includes(normalizedQuery))
      .slice(0, 50);
  }, [options, query, normalizedQuery]);

  const hasExactMatch = useMemo(
    () => options.some((option) => option.toLowerCase() === normalizedQuery),
    [options, normalizedQuery]
  );

  const showAddNew = Boolean(query) && !hasExactMatch;

  const selectOption = (option: string) => {
    setInputText(option);
    onChange(option);
    setOpen(false);
  };

  const selectNewOption = () => {
    const next = query;
    saveCustomDepartment(storageKey, defaultOptions, next);
    setOptions(loadStoredDepartments(storageKey, defaultOptions));
    selectOption(next);
  };

  const handleInputChange = (next: string) => {
    setInputText(next);
    setOpen(true);
    if (!next.trim()) {
      onChange('');
    }
  };

  const commitInput = () => {
    const q = inputText.trim();
    if (!q) {
      onChange('');
      return;
    }

    const existing = options.find((option) => option.toLowerCase() === q.toLowerCase());
    if (existing) {
      selectOption(existing);
      return;
    }

    selectNewOption();
  };

  const handleBlur = () => {
    window.setTimeout(() => {
      setOpen(false);
      commitInput();
    }, 150);
  };

  const panelClass = cn(
    'absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border shadow-lg',
    isDark ? 'border-white/15 bg-[#0f172a]' : 'border-[hsl(var(--border))] bg-[hsl(var(--card))]'
  );

  return (
    <div ref={rootRef} className="relative space-y-1.5">
      <label className={labelClassName} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={inputText}
        placeholder={placeholder}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        className={cn(inputClassName, 'uppercase')}
        autoComplete="off"
      />
      {open && (
        <ul className={panelClass} role="listbox">
          {matchingOptions.length === 0 && !showAddNew && (
            <li className="px-3 py-2 text-sm text-[hsl(var(--muted-foreground))]">No departments found</li>
          )}
          {matchingOptions.map((option) => (
            <li key={option}>
              <button
                type="button"
                role="option"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectOption(option)}
                className={cn(
                  'w-full px-3 py-2 text-left text-sm font-semibold uppercase hover:bg-[#0088cc]/10',
                  isDark && 'hover:bg-[#0088cc]/20'
                )}
              >
                {option}
              </button>
            </li>
          ))}
          {showAddNew && (
            <li>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={selectNewOption}
                className="w-full px-3 py-2 text-left text-sm font-bold text-[#0088cc] hover:bg-[#0088cc]/10 dark:hover:bg-[#0088cc]/20"
              >
                + Add new department: {query.toUpperCase()}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
